import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader, AppShell, Button, ShellPanel } from '@origo/origo-ui';
import { Map, useMapViewContext } from '@origo/map-context';
import Point from '@arcgis/core/geometry/Point.js';
import { useAppSelector } from '../store/storeHooks';
import { selectUser } from '@origo/auth';
import geodataLogo from '../assets/geodata-logo.svg';
import { VersionTrigger, ChangelogDialog, NewVersionNotice, useNewVersionNotice } from './VersionInfo';
import {
    AddressSearch,
    useAddressSearch,
    SearchSuggestionLayer,
    type AddressSuggestion,
} from '@origo/origo-components';
import changelogMarkdown from '../../CHANGELOG.md?raw';
import PlanregisterView from '../features/planregister/PlanregisterView';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
    onSignOut?: () => void;
    /** ArcGIS WebMap-id som vises i kart-kolonnen. */
    webMapId?: string;
}

function getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

const AppLayout: React.FC<AppLayoutProps> = ({ onSignOut, webMapId }) => {
    const { t } = useTranslation();
    const user = useAppSelector(selectUser);
    const { mapView } = useMapViewContext();
    const [changelogOpen, setChangelogOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const { hasNewVersion, dismiss } = useNewVersionNotice();

    // Adresse-/gnr-bnr-søk i headeren (Kartverket via origo-components), samme
    // mønster som drømmeplan-admin. Treff zoomer kartet og markeres med pin.
    const { input, suggestions, isFetchingDetails, handleInputChange, handleSuggestionClick } =
        useAddressSearch({});

    const onSuggestionClick = useCallback((suggestion: AddressSuggestion) => {
        handleSuggestionClick(suggestion);
        mapView?.goTo({
            target: new Point({ x: suggestion.x, y: suggestion.y, spatialReference: { wkid: suggestion.wkid } }),
            zoom: 16,
        });
    }, [handleSuggestionClick, mapView]);

    // PlanregisterView holder route-state internt; vi får tilbake en
    // "go-home"-callback via registrering. AppHeader sin onTitleClick
    // bruker denne for å sende brukeren tilbake til landing.
    const goHomeRef = useRef<(() => void) | null>(null);
    const handleGoHomeRegister = useCallback((handler: (() => void) | null) => {
        goHomeRef.current = handler;
    }, []);
    const handleTitleClick = useCallback(() => {
        goHomeRef.current?.();
    }, []);

    const openChangelog = () => {
        setChangelogOpen(true);
        dismiss();
    };

    return (
        <AppShell
            className={styles.appShell}
            header={
                <AppHeader
                    logo={<img src={geodataLogo} alt="Geodata" width={34} height={32} />}
                    title={t('appTitle')}
                    titleSize="sm"
                    subtitle={t('appSubtitle')}
                    onTitleClick={handleTitleClick}
                    center={
                        <AddressSearch
                            input={input}
                            suggestions={suggestions}
                            isFetchingDetails={isFetchingDetails}
                            onInputChange={handleInputChange}
                            onSuggestionClick={onSuggestionClick}
                            onHoverChange={setHoveredIndex}
                        />
                    }
                    user={user?.fullName ? {
                        name: user.fullName,
                        initials: getInitials(user.fullName),
                        avatarUrl: user.thumbnail,
                    } : undefined}
                    actions={
                        <>
                            <VersionTrigger placement="header" onClick={openChangelog} />
                            {onSignOut && (
                                <Button variant="ghost" size="sm" onClick={onSignOut}>
                                    {t('common.logout')}
                                </Button>
                            )}
                        </>
                    }
                />
            }
        >
            <div className={styles.mainColumn}>
                {hasNewVersion && (
                    <NewVersionNotice onOpenChangelog={openChangelog} onDismiss={dismiss} />
                )}
                <div className={styles.body}>
                    <ShellPanel
                        resizable
                        defaultWidthVw={66.667}
                        minWidth={520}
                        maxWidthVw={85}
                    >
                        <PlanregisterView onGoHomeRegister={handleGoHomeRegister} />
                    </ShellPanel>
                    <div className={styles.mapArea}>
                        <div className={styles.mapContainer}>
                            <Map
                                webMapId={webMapId}
                                tools2D={['fullscreen', 'scalebar']}
                                disablePopup
                                disableClick={false}
                            />
                            <SearchSuggestionLayer
                                suggestions={suggestions}
                                hoveredIndex={hoveredIndex}
                                onPinClick={onSuggestionClick}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ChangelogDialog
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                appName="Planregister-redaktøren"
                changelogMarkdown={changelogMarkdown}
            />
        </AppShell>
    );
};

export default AppLayout;
